/**
 * KidsRadar widget -- Parental overview of child profiles.
 * Only shown if user has child profiles.
 */

import type { Widget, WidgetContext } from '../types';

interface ChildProfile {
  id: string;
  name: string;
  avatar: string;
  timeToday: number; // minutes
  timeLimit: number; // minutes, 0 = unlimited
  gamesPlayed: number;
  cardsReviewed: number;
  paused: boolean;
}

function getMockChildren(): ChildProfile[] {
  try {
    const raw = localStorage.getItem('aweni_child_profiles');
    if (raw) return JSON.parse(raw);
  } catch { /* */ }
  return [];
}

function getTimeColor(used: number, limit: number): string {
  if (limit === 0) return '#0A0A0A'; // unlimited
  const pct = used / limit;
  if (pct > 1) return '#EF4444';
  if (pct > 0.75) return '#F97316';
  return '#0A0A0A';
}

function formatMinutes(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0) return `${h}h${m > 0 ? ` ${m}m` : ''}`;
  return `${m}m`;
}

export const KidsRadar: Widget = {
  id: 'kids-radar',
  label: { en: 'Kids Radar', fr: 'Radar enfants', ar: 'رادار الأطفال', es: 'Radar de hijos', de: 'Kinder-Radar' },
  icon: '👨‍👩‍👧‍👦',
  defaultVisible: true,
  defaultOrder: 8,
  defaultSize: 'compact',

  visibilityRule: () => {
    const children = getMockChildren();
    return children.length > 0;
  },

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    const children = getMockChildren();
    if (children.length === 0) {
      container.innerHTML = '';
      return;
    }

    if (size === 'compact') {
      container.innerHTML = `
        <div class="rounded-2xl p-4 cursor-pointer" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="parental">
          <div class="flex items-center gap-3 overflow-x-auto">
            ${children.map((child) => `
              <div class="flex items-center gap-2 flex-shrink-0">
                <span style="font-size:20px;">${child.avatar || '👧'}</span>
                <div>
                  <p class="text-xs font-medium" style="color:var(--text-primary);">${child.name}</p>
                  <p class="text-xs" style="color:${getTimeColor(child.timeToday, child.timeLimit)};">${formatMinutes(child.timeToday)}</p>
                </div>
              </div>
            `).join('<div style="width:1px;height:24px;background:var(--border);flex-shrink:0;"></div>')}
          </div>
        </div>`;
    } else {
      container.innerHTML = `
        <div class="rounded-2xl p-5" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
          <h3 class="text-sm font-semibold uppercase tracking-wide mb-3" style="color:var(--text-secondary);">👨‍👩‍👧‍👦 ${ctx.t('widget_kids_radar')}</h3>
          <div class="space-y-3">
            ${children.map((child) => `
              <div class="flex items-center justify-between p-3 rounded-xl" style="background:var(--bg-secondary);">
                <div class="flex items-center gap-3">
                  <span style="font-size:24px;">${child.avatar || '👧'}</span>
                  <div>
                    <p class="text-sm font-medium" style="color:var(--text-primary);">${child.name}</p>
                    <p class="text-xs" style="color:var(--text-secondary);">🎮 ${child.gamesPlayed} · 📚 ${child.cardsReviewed}</p>
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-sm font-bold" style="color:${getTimeColor(child.timeToday, child.timeLimit)};">
                    ${formatMinutes(child.timeToday)}${child.timeLimit > 0 ? '/' + formatMinutes(child.timeLimit) : ''}
                  </span>
                  <button class="pause-btn text-xs px-2 py-1 rounded-lg" style="background:${child.paused ? '#0A0A0A' : '#EF4444'}20;color:${child.paused ? '#0A0A0A' : '#EF4444'};" data-child-id="${child.id}">
                    ${child.paused ? ctx.t('parental_resume') : ctx.t('parental_pause_now')}
                  </button>
                </div>
              </div>
            `).join('')}
          </div>
        </div>`;
    }

    container.querySelectorAll<HTMLElement>('[data-nav]').forEach((el) => {
      el.addEventListener('click', () => ctx.navigate('parental'));
    });
  },
};
